import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, CalendarDays, ChevronLeft, Clock3, Droplets, Dumbbell,
  FileText, Flame, Footprints, Gauge, HeartPulse, LineChart, Plus, Route, Settings2,
  ShieldCheck, Target, Trash2, Utensils, X,
} from 'lucide-react';
import { fitnessApi } from '../api';
import { WellnessTabBar, WellnessWorkspace } from './WellnessCenter';

interface Props {
  onClose: () => void;
  onNavigateWorkspace?: (w: WellnessWorkspace) => void;
  initialAction?: 'record';
}

interface ExerciseSet {
  exercise_name: string;
  set_number: number;
  weight_kg: number | null;
  reps: number | null;
  duration_seconds: number | null;
}

interface ExerciseSession {
  id: number;
  exercise_type: string;
  exercise_type_label: string;
  title: string;
  started_at: string;
  duration_minutes: number;
  calories: number;
  distance_km: number | null;
  average_heart_rate: number | null;
  intensity: string;
  intensity_label: string;
  feeling: number;
  note: string;
  sets: ExerciseSet[];
}

const TYPES = [
  ['strength', '力量训练'], ['running', '跑步'], ['cycling', '骑行'],
  ['swimming', '游泳'], ['ball', '球类'], ['walking', '健走'],
  ['hiit', 'HIIT'], ['mobility', '拉伸恢复'], ['other', '其他'],
] as const;

const typeLabel = (type: string) => TYPES.find(([key]) => key === type)?.[1] || '运动';
const asArray = <T,>(value: unknown): T[] => Array.isArray(value) ? value : [];
const normalizeSessions = (payload: any): ExerciseSession[] => {
  const rows = Array.isArray(payload)
    ? payload
    : payload?.sessions || payload?.results || payload?.items || payload?.data;
  return asArray<ExerciseSession>(rows).map((item) => ({
    ...item,
    sets: asArray<ExerciseSet>(item?.sets),
  }));
};
const localDateTime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

function MiniBars({ data }: { data: any[] }) {
  const max = Math.max(30, ...data.map((item) => item.minutes || 0));
  return (
    <div className="ex-bars" aria-label="近七日运动时长">
      {data.map((item) => {
        const date = new Date(`${item.date}T00:00:00`);
        return (
          <div key={item.date} className="ex-bar-item">
            <span>{item.minutes || 0}</span>
            <div><i style={{ height: `${Math.max(4, ((item.minutes || 0) / max) * 100)}%` }} /></div>
            <small>{['日', '一', '二', '三', '四', '五', '六'][date.getDay()]}</small>
          </div>
        );
      })}
    </div>
  );
}

function Progress({ value, target, tone = 'blue' }: { value: number; target: number; tone?: string }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  return <div className={`ex-progress ${tone}`}><i style={{ width: `${pct}%` }} /></div>;
}

function CorrelationChart({ data }: { data: any[] }) {
  const points = asArray<any>(data);
  const width = 640;
  const height = 150;
  const maxCalories = Math.max(500, ...points.flatMap((item) => [item.intake_calories || 0, item.exercise_calories || 0]));
  const step = points.length > 1 ? width / (points.length - 1) : width;
  const line = (key: string) => points.map((item, index) => {
    const x = index * step;
    const y = height - ((item[key] || 0) / maxCalories) * (height - 16) - 8;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="ex-correlation-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="饮食摄入与运动消耗趋势">
        {[0.25, 0.5, 0.75].map((ratio) => <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="grid" />)}
        <polyline points={line('intake_calories')} className="intake" />
        <polyline points={line('exercise_calories')} className="exercise" />
      </svg>
      <div className="ex-chart-labels">{points.map((item) => <span key={item.date}>{Number(item.date.slice(-2))}</span>)}</div>
    </div>
  );
}

function RecordDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<any>({
    exercise_type: 'strength', title: '力量训练', started_at: localDateTime(),
    duration_minutes: '45', calories: '300', distance_km: '', average_heart_rate: '',
    intensity: 'moderate', feeling: '3', note: '',
  });
  const [sets, setSets] = useState<Array<{ exercise_name: string; weight_kg: string; reps: string }>>([
    { exercise_name: '深蹲', weight_kg: '', reps: '10' },
  ]);
  const strength = form.exercise_type === 'strength';

  const setField = (key: string, value: string) => setForm((prev: any) => ({ ...prev, [key]: value }));
  const changeType = (value: string) => setForm((prev: any) => ({
    ...prev, exercise_type: value, title: typeLabel(value),
  }));
  const apiErrorMessage = (err: any) => {
    const detail = err?.response?.data?.detail;
    if (detail) return String(detail);
    if (err?.response?.status) return `保存失败（HTTP ${err.response.status}）`;
    if (err?.code === 'ECONNABORTED') return '保存超时，请检查网络后重试';
    return err?.message || '保存训练记录失败';
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fitnessApi.createExerciseSession({
        ...form,
        duration_minutes: Number(form.duration_minutes),
        calories: Number(form.calories || 0),
        distance_km: form.distance_km ? Number(form.distance_km) : null,
        average_heart_rate: form.average_heart_rate ? Number(form.average_heart_rate) : null,
        feeling: Number(form.feeling),
        sets: strength ? sets.filter((item) => item.exercise_name.trim()).map((item, index) => ({
          exercise_name: item.exercise_name.trim(),
          set_number: index + 1,
          weight_kg: item.weight_kg ? Number(item.weight_kg) : null,
          reps: item.reps ? Number(item.reps) : null,
        })) : [],
      });
      onClose();
      // 记录已保存后再刷新看板。刷新异常不应被误报为保存失败。
      void onSaved().catch((refreshError) => {
        console.error('训练记录已保存，但刷新运动看板失败:', refreshError);
      });
    } catch (err: any) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ex-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="ex-dialog" onSubmit={submit}>
        <header><div><small>训练日志</small><h2>记录一次运动</h2></div><button type="button" onClick={onClose}><X size={18} /></button></header>
        <div className="ex-form-grid">
          <label>运动类型<select value={form.exercise_type} onChange={(e) => changeType(e.target.value)}>{TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
          <label>训练标题<input required value={form.title} onChange={(e) => setField('title', e.target.value)} /></label>
          <label className="wide">开始时间<input required type="datetime-local" value={form.started_at} onChange={(e) => setField('started_at', e.target.value)} /></label>
          <label>时长（分钟）<input required min="1" type="number" value={form.duration_minutes} onChange={(e) => setField('duration_minutes', e.target.value)} /></label>
          <label>消耗（kcal）<input min="0" type="number" value={form.calories} onChange={(e) => setField('calories', e.target.value)} /></label>
          {!strength && <label>距离（km）<input min="0" step="0.01" type="number" value={form.distance_km} onChange={(e) => setField('distance_km', e.target.value)} /></label>}
          <label>平均心率<input min="30" max="240" type="number" value={form.average_heart_rate} onChange={(e) => setField('average_heart_rate', e.target.value)} placeholder="可选" /></label>
          <label>训练强度<select value={form.intensity} onChange={(e) => setField('intensity', e.target.value)}><option value="low">低强度</option><option value="moderate">中等强度</option><option value="high">高强度</option></select></label>
          <label>训练感受<select value={form.feeling} onChange={(e) => setField('feeling', e.target.value)}><option value="1">1 很吃力</option><option value="2">2 偏累</option><option value="3">3 适中</option><option value="4">4 状态好</option><option value="5">5 极佳</option></select></label>
        </div>
        {strength && (
          <section className="ex-set-editor">
            <div><b>力量动作</b><button type="button" onClick={() => setSets((prev) => [...prev, { exercise_name: '', weight_kg: '', reps: '' }])}><Plus size={14} />动作</button></div>
            {sets.map((item, index) => (
              <div className="ex-set-row" key={index}>
                <span>{index + 1}</span>
                <input value={item.exercise_name} onChange={(e) => setSets((prev) => prev.map((row, i) => i === index ? { ...row, exercise_name: e.target.value } : row))} placeholder="动作名称" />
                <input type="number" value={item.weight_kg} onChange={(e) => setSets((prev) => prev.map((row, i) => i === index ? { ...row, weight_kg: e.target.value } : row))} placeholder="kg" />
                <input type="number" value={item.reps} onChange={(e) => setSets((prev) => prev.map((row, i) => i === index ? { ...row, reps: e.target.value } : row))} placeholder="次数" />
                <button type="button" onClick={() => setSets((prev) => prev.filter((_, i) => i !== index))}><X size={14} /></button>
              </div>
            ))}
          </section>
        )}
        <label className="ex-note">训练备注<textarea rows={3} value={form.note} onChange={(e) => setField('note', e.target.value)} placeholder="身体状态、训练重点或不适情况" /></label>
        {error && <p className="ex-error">{error}</p>}
        <footer><button type="button" onClick={onClose}>取消</button><button className="primary" disabled={saving}>{saving ? '保存中...' : '保存训练'}</button></footer>
      </form>
    </div>
  );
}

function GoalDialog({ goal, onClose, onSaved }: { goal: any; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    weekly_sessions: String(goal.weekly_sessions),
    weekly_minutes: String(goal.weekly_minutes),
    weekly_calories: String(goal.weekly_calories),
    primary_goal: goal.primary_goal || 'general',
    max_heart_rate: String(goal.max_heart_rate || 190),
  });
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await fitnessApi.updateExerciseGoal({
      weekly_sessions: Number(form.weekly_sessions),
      weekly_minutes: Number(form.weekly_minutes),
      weekly_calories: Number(form.weekly_calories),
      primary_goal: form.primary_goal,
      max_heart_rate: Number(form.max_heart_rate),
    });
    await onSaved();
    onClose();
  };
  return (
    <div className="ex-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="ex-dialog compact" onSubmit={submit}>
        <header><div><small>训练计划</small><h2>设置每周目标</h2></div><button type="button" onClick={onClose}><X size={18} /></button></header>
        <div className="ex-form-grid">
          <label>训练次数<input min="1" type="number" value={form.weekly_sessions} onChange={(e) => setForm({ ...form, weekly_sessions: e.target.value })} /></label>
          <label>训练分钟<input min="30" type="number" value={form.weekly_minutes} onChange={(e) => setForm({ ...form, weekly_minutes: e.target.value })} /></label>
          <label>消耗目标（kcal）<input min="100" type="number" value={form.weekly_calories} onChange={(e) => setForm({ ...form, weekly_calories: e.target.value })} /></label>
          <label>主要目标<select value={form.primary_goal} onChange={(e) => setForm({ ...form, primary_goal: e.target.value })}><option value="general">综合健康</option><option value="fat_loss">减脂</option><option value="muscle_gain">增肌</option><option value="endurance">提升心肺</option></select></label>
          <label className="wide">最大心率（次/分）<input min="120" max="230" type="number" value={form.max_heart_rate} onChange={(e) => setForm({ ...form, max_heart_rate: e.target.value })} /><small>用于计算个性化心率区间，可依据运动测试或医生建议调整。</small></label>
        </div>
        <footer><button type="button" onClick={onClose}>取消</button><button className="primary">保存目标</button></footer>
      </form>
    </div>
  );
}

const FitnessCenter: React.FC<Props> = ({ onClose, onNavigateWorkspace, initialAction }) => {
  const [dashboard, setDashboard] = useState<any>(null);
  const [sessions, setSessions] = useState<ExerciseSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState('');
  const [recordOpen, setRecordOpen] = useState(initialAction === 'record');
  const [goalOpen, setGoalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadWarning('');
    try {
      const [dashboardResult, sessionsResult] = await Promise.allSettled([
        fitnessApi.getExerciseDashboard(), fitnessApi.getExerciseSessions(),
      ]);
      if (dashboardResult.status === 'fulfilled') {
        const payload = dashboardResult.value.data || {};
        setDashboard({
          ...payload,
          trend: asArray(payload.trend),
          recommendations: asArray(payload.recommendations),
          recent_sessions: normalizeSessions(payload.recent_sessions),
        });
      } else {
        setDashboard(null);
      }
      if (sessionsResult.status === 'fulfilled') {
        setSessions(normalizeSessions(sessionsResult.value.data));
      } else {
        setSessions([]);
      }
      if (dashboardResult.status === 'rejected' || sessionsResult.status === 'rejected') {
        setLoadWarning('部分训练数据暂时不可用，请稍后刷新');
      }
    } catch {
      setDashboard(null);
      setSessions([]);
      setLoadWarning('训练数据加载失败，请稍后刷新');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibleSessions = useMemo(
    () => asArray<ExerciseSession>(sessions).filter((item) => !filter || item.exercise_type === filter),
    [sessions, filter],
  );
  const remove = async (id: number) => {
    if (!window.confirm('删除这条训练记录？')) return;
    await fitnessApi.deleteExerciseSession(id);
    await load();
  };

  const week = dashboard?.week || {
    sessions: 0,
    minutes: 0,
    calories: 0,
    streak: 0,
    goal: {
      weekly_sessions: 3,
      weekly_minutes: 150,
      weekly_calories: 1000,
      primary_goal: 'general',
      primary_goal_label: '综合健康',
      max_heart_rate: 190,
    },
  };
  const sessionPct = Math.min(100, Math.round((week.sessions / week.goal.weekly_sessions) * 100));
  const insight = dashboard?.insight;

  return (
    <div className="ex-root">
      <header className="ex-header">
        <button onClick={onClose} aria-label="返回"><ChevronLeft size={19} /></button>
        <div><h1>运动训练</h1><small>本周完成度 {sessionPct}%</small></div>
        <button onClick={() => setRecordOpen(true)} aria-label="记录训练"><Plus size={19} /></button>
      </header>
      {onNavigateWorkspace && <WellnessTabBar active="fitness" onNavigate={(w) => w !== 'fitness' && onNavigateWorkspace(w)} />}

      <main className="ex-main">
        {loadWarning && <div className="ex-warning">{loadWarning}<button onClick={load}>重新加载</button></div>}
        {loading ? <div className="ex-loading">正在加载训练数据...</div> : (
          <>
            <section className="ex-week-summary">
              <div className="ex-week-lead">
                <span>本周训练</span>
                <strong>{week.sessions}<small> / {week.goal.weekly_sessions} 次</small></strong>
                <Progress value={week.sessions} target={week.goal.weekly_sessions} />
              </div>
              <div><Clock3 size={17} /><span><b>{week.minutes}</b><small>分钟</small></span></div>
              <div><Flame size={17} /><span><b>{week.calories}</b><small>kcal</small></span></div>
              <div><Activity size={17} /><span><b>{week.streak}</b><small>连续天</small></span></div>
            </section>

            {insight && (
              <section className="ex-readiness">
                <div className="ex-readiness-score">
                  <small>今日训练准备度</small>
                  <strong>{insight.score}<span>/100</span></strong>
                  <b className={insight.score >= 72 ? 'good' : insight.score >= 50 ? 'moderate' : 'caution'}>{insight.level}</b>
                  <div className="ex-score-track"><i style={{ width: `${insight.score}%` }} /></div>
                </div>
                <div className="ex-readiness-plan">
                  <header><div><small>个性化建议</small><h2>{insight.recommended_intensity} · {insight.recommended_duration}</h2></div><ShieldCheck size={19} /></header>
                  <div className="ex-type-tags">{asArray<string>(insight.recommended_types).map((item) => <span key={item}>{item}</span>)}</div>
                  <p>{asArray<any>(insight.suggestions)[0]?.detail}</p>
                </div>
                <div className="ex-source-state">
                  <small>分析数据源</small>
                  <div><span className={insight.sources?.report ? 'on' : ''}><FileText size={14} />体检</span><span className={insight.sources?.health ? 'on' : ''}><HeartPulse size={14} />健康</span><span className={insight.sources?.nutrition ? 'on' : ''}><Utensils size={14} />饮食</span><span className={insight.sources?.exercise ? 'on' : ''}><Activity size={14} />训练</span></div>
                </div>
              </section>
            )}

            {insight && (asArray<any>(insight.restrictions).length > 0 || asArray<any>(insight.nutrition_actions).length > 0) && (
              <div className="ex-analysis-grid">
                <section className="ex-panel ex-constraints">
                  <header><div><small>训练约束</small><h2>今天需要注意</h2></div><AlertTriangle size={18} /></header>
                  {asArray<any>(insight.restrictions).length ? asArray<any>(insight.restrictions).slice(0, 3).map((item) => (
                    <div key={item.code} className={item.severity}><span><AlertTriangle size={14} /></span><div><b>{item.title}</b><small>{item.detail}</small></div></div>
                  )) : <div className="ex-analysis-ok"><ShieldCheck size={16} />当前没有明显训练限制</div>}
                </section>
                <section className="ex-panel ex-fueling">
                  <header><div><small>近期饮食关联</small><h2>训练供能建议</h2></div><Utensils size={18} /></header>
                  <div className="ex-nutrition-snapshot">
                    <span><Flame size={14} /><b>{insight.nutrition?.calories || 0}</b><small>kcal</small></span>
                    <span><Dumbbell size={14} /><b>{insight.nutrition?.protein || 0}</b><small>g 蛋白质</small></span>
                    <span><Droplets size={14} /><b>{insight.nutrition?.water_cups || 0}</b><small>杯水</small></span>
                  </div>
                  {asArray<any>(insight.nutrition_actions).slice(0, 2).map((item, index) => <p key={`${item.title}-${index}`}><b>{item.title}</b>{item.detail}</p>)}
                </section>
              </div>
            )}

            {insight?.training_load && (
              <div className="ex-performance-grid">
                <section className="ex-panel ex-load-recovery">
                  <header>
                    <div><small>训练负荷与恢复</small><h2>身体响应</h2></div>
                    <Activity size={18} />
                  </header>
                  <div className="ex-performance-scores">
                    <div>
                      <span>恢复指数</span>
                      <strong>{insight.recovery?.score}<small>/100</small></strong>
                      <b className={insight.recovery?.score >= 75 ? 'good' : insight.recovery?.score >= 50 ? 'moderate' : 'caution'}>{insight.recovery?.label}</b>
                      <div className="ex-score-track recovery"><i style={{ width: `${insight.recovery?.score || 0}%` }} /></div>
                    </div>
                    <div>
                      <span>近 7 日负荷</span>
                      <strong>{insight.training_load.acute_load}<small>点</small></strong>
                      <b className={insight.training_load.status}>{insight.training_load.label}</b>
                      <div className="ex-load-comparison">
                        <span>前 7 日 {insight.training_load.previous_load}</span>
                        <span>{insight.training_load.ratio ? `${insight.training_load.ratio}x` : '等待基线'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ex-response-facts">
                    <span><b>{insight.recovery?.average_feeling || '—'}</b>近期训练感受</span>
                    <span><b>{insight.recovery?.resting_heart_rate || '—'}</b>静息心率</span>
                    <span><b>{insight.training_load.recent_2d_load}</b>近 2 日负荷</span>
                  </div>
                </section>

                <section className="ex-panel ex-heart-zones">
                  <header>
                    <div><small>个性化强度</small><h2>心率训练区间</h2></div>
                    <button onClick={() => setGoalOpen(true)} aria-label="设置最大心率"><Settings2 size={16} /></button>
                  </header>
                  <div className="ex-zone-summary">
                    <span>最大心率 <b>{insight.heart_rate?.max_heart_rate}</b></span>
                    <span>静息心率 <b>{insight.heart_rate?.resting_heart_rate}</b></span>
                  </div>
                  <div className="ex-zone-list">
                    {asArray<any>(insight.heart_rate?.zones).map((zone, index) => (
                      <div key={zone.zone}>
                        <b>{zone.zone}</b>
                        <span>{zone.label}</span>
                        <i><em style={{ width: `${30 + index * 14}%`, background: zone.color }} /></i>
                        <strong>{zone.min}–{zone.max}</strong>
                      </div>
                    ))}
                  </div>
                  <p>{insight.heart_rate?.source} · 单位：次/分</p>
                </section>
              </div>
            )}

            {insight?.correlation && (
              <div className="ex-performance-grid">
                <section className="ex-panel ex-correlation">
                  <header>
                    <div><small>近 14 日关联</small><h2>摄入与运动消耗</h2></div>
                    <LineChart size={18} />
                  </header>
                  <CorrelationChart data={asArray<any>(insight.correlation.series)} />
                  <div className="ex-chart-legend">
                    <span><i className="intake" />饮食摄入</span>
                    <span><i className="exercise" />运动消耗</span>
                    <b>{insight.correlation.days_with_nutrition} 天饮食数据</b>
                  </div>
                  <p>{insight.correlation.summary}</p>
                </section>

                <section className="ex-panel ex-strategy">
                  <header>
                    <div><small>{week.goal.primary_goal_label || insight.strategy?.label}</small><h2>{insight.strategy?.label}</h2></div>
                    <Target size={18} />
                  </header>
                  <strong>{insight.strategy?.headline}</strong>
                  <div className="ex-strategy-focus"><span>重点心率区间</span><b>{insight.strategy?.focus_zone}</b></div>
                  <div className="ex-strategy-columns">
                    <div><small>训练安排</small>{asArray<string>(insight.strategy?.training).slice(0, 3).map((item) => <p key={item}>{item}</p>)}</div>
                    <div><small>营养配合</small>{asArray<string>(insight.strategy?.nutrition).slice(0, 3).map((item) => <p key={item}>{item}</p>)}</div>
                  </div>
                </section>
              </div>
            )}

            <div className="ex-dashboard-grid">
              <section className="ex-panel ex-trend">
                <header><div><small>训练负荷</small><h2>近 7 日运动时长</h2></div><span>{week.minutes} / {week.goal.weekly_minutes} 分钟</span></header>
                <MiniBars data={asArray(dashboard?.trend)} />
                <Progress value={week.minutes} target={week.goal.weekly_minutes} tone="green" />
              </section>

              <section className="ex-panel ex-goal">
                <header><div><small>本周计划</small><h2>目标进度</h2></div><button onClick={() => setGoalOpen(true)} aria-label="设置目标"><Settings2 size={16} /></button></header>
                <div className="ex-goal-row"><span><Target size={15} />训练次数</span><b>{week.sessions}/{week.goal.weekly_sessions}</b></div>
                <Progress value={week.sessions} target={week.goal.weekly_sessions} />
                <div className="ex-goal-row"><span><Clock3 size={15} />运动时长</span><b>{week.minutes}/{week.goal.weekly_minutes}</b></div>
                <Progress value={week.minutes} target={week.goal.weekly_minutes} tone="green" />
                <div className="ex-goal-row"><span><Flame size={15} />热量消耗</span><b>{week.calories}/{week.goal.weekly_calories}</b></div>
                <Progress value={week.calories} target={week.goal.weekly_calories} tone="amber" />
              </section>
            </div>

            <section className="ex-panel ex-today">
              <header><div><small>今日训练</small><h2>推荐训练</h2></div><button className="ex-primary-action" onClick={() => setRecordOpen(true)}><Plus size={15} />记录训练</button></header>
              <div className="ex-recommendations">
                {asArray<any>(dashboard?.recommendations).length ? asArray<any>(dashboard?.recommendations).map((item: any) => (
                  <div key={item.id}><span><Dumbbell size={17} /></span><div><b>{item.title.replace(/^\\S+\\s*/, '')}</b><small>{item.category} · {item.description}</small></div></div>
                )) : <div className="ex-empty-inline"><Footprints size={18} /><span>今天没有固定任务，可安排 30 分钟自主训练</span></div>}
              </div>
            </section>

            <div className="ex-dashboard-grid lower">
              <section className="ex-panel ex-recent">
                <header><div><small>训练日志</small><h2>最近训练</h2></div><button onClick={() => setHistoryOpen(true)}>全部记录</button></header>
                {asArray<ExerciseSession>(dashboard?.recent_sessions).length ? asArray<ExerciseSession>(dashboard?.recent_sessions).slice(0, 3).map((item: ExerciseSession) => (
                  <div className="ex-session-row" key={item.id}>
                    <span className="ex-session-icon">{item.exercise_type === 'strength' ? <Dumbbell size={17} /> : <Route size={17} />}</span>
                    <div><b>{item.title}</b><small>{new Date(item.started_at).toLocaleDateString('zh-CN')} · {item.intensity_label}</small></div>
                    <strong>{item.duration_minutes}<small>分钟</small></strong>
                  </div>
                )) : <div className="ex-empty">记录第一次训练后，这里会形成你的运动轨迹。</div>}
              </section>

              <section className="ex-panel ex-health">
                <header><div><small>身体表现</small><h2>健康关联</h2></div><HeartPulse size={18} /></header>
                <div><span><Gauge size={17} />当前体重</span><b>{dashboard?.health_context?.weight ? `${dashboard.health_context.weight} kg` : '暂无数据'}</b></div>
                <div><span><HeartPulse size={17} />静息心率</span><b>{dashboard?.health_context?.resting_heart_rate ? `${dashboard.health_context.resting_heart_rate} 次/分` : '暂无数据'}</b></div>
                <p>训练量会与身体管理中的体重、静息心率持续关联，用于观察长期变化。</p>
              </section>
            </div>

            {insight && (
              <section className="ex-panel ex-evidence">
                <header><div><small>可解释分析</small><h2>建议依据</h2></div><span>{asArray<any>(insight.evidence).length} 项数据</span></header>
                <div>
                  {asArray<any>(insight.evidence).map((item, index) => (
                    <article key={`${item.label}-${index}`}><span>{item.source}</span><b>{item.label}</b><strong>{item.value}</strong><small>{item.date}</small></article>
                  ))}
                  {!asArray<any>(insight.evidence).length && <div className="ex-empty">补充体检与健康数据后，将显示详细分析依据。</div>}
                </div>
                <p>{insight.disclaimer}</p>
              </section>
            )}
          </>
        )}
      </main>

      {recordOpen && <RecordDialog onClose={() => setRecordOpen(false)} onSaved={load} />}
      {goalOpen && <GoalDialog goal={week.goal} onClose={() => setGoalOpen(false)} onSaved={load} />}
      {historyOpen && (
        <div className="ex-overlay" onMouseDown={(event) => event.target === event.currentTarget && setHistoryOpen(false)}>
          <section className="ex-history">
            <header><div><small>运动档案</small><h2>训练历史</h2></div><button onClick={() => setHistoryOpen(false)}><X size={18} /></button></header>
            <div className="ex-history-filter">
              <button className={!filter ? 'active' : ''} onClick={() => setFilter('')}>全部</button>
              {TYPES.slice(0, 5).map(([key, label]) => <button key={key} className={filter === key ? 'active' : ''} onClick={() => setFilter(key)}>{label}</button>)}
            </div>
            <div className="ex-history-list">
              {visibleSessions.map((item) => (
                <article key={item.id}>
                  <div className="ex-history-date"><CalendarDays size={16} /><span>{new Date(item.started_at).toLocaleDateString('zh-CN')}</span></div>
                  <div className="ex-history-main"><div><b>{item.title}</b><small>{item.exercise_type_label} · {item.intensity_label}</small></div><strong>{item.duration_minutes} 分钟</strong></div>
                  <div className="ex-history-meta"><span>{item.calories} kcal</span>{item.distance_km && <span>{item.distance_km} km</span>}{item.average_heart_rate && <span>{item.average_heart_rate} bpm</span>}<span>感受 {item.feeling}/5</span></div>
                  {asArray<ExerciseSet>(item.sets).length > 0 && <p>{asArray<ExerciseSet>(item.sets).map((set) => `${set.exercise_name} ${set.weight_kg || 0}kg×${set.reps || 0}`).join(' · ')}</p>}
                  <button onClick={() => remove(item.id)} aria-label={`删除${item.title}`}><Trash2 size={15} /></button>
                </article>
              ))}
              {!visibleSessions.length && <div className="ex-empty">暂无训练记录</div>}
            </div>
          </section>
        </div>
      )}

      <style>{`
        .ex-root{min-height:100vh;background:#050914;color:#e5e7eb;padding-bottom:calc(36px + var(--safe-area-inset-bottom,0px))}
        .ex-header{min-height:72px;display:flex;align-items:center;gap:14px;padding:calc(10px + var(--safe-area-inset-top,0px)) 16px 8px;background:#070c18}
        .ex-header>button,.ex-panel header>button,.ex-dialog header>button,.ex-history header>button{width:34px;height:34px;display:grid;place-items:center;border:1px solid #243044;border-radius:8px;background:#0d1525;color:#cbd5e1;cursor:pointer}
        .ex-header>div{flex:1}.ex-header h1{margin:0;font-size:17px}.ex-header small{font-size:10px;color:#64748b}
        .ex-main{max-width:1000px;margin:0 auto;padding:18px 16px 80px}.ex-loading,.ex-empty{padding:44px;text-align:center;color:#64748b}
        .ex-warning{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;padding:10px 12px;border:1px solid #854d0e;border-radius:7px;background:#21170a;color:#fbbf24;font-size:10px}.ex-warning button{border:0;background:transparent;color:#7dd3fc;font:inherit;cursor:pointer}
        .ex-week-summary{display:grid;grid-template-columns:minmax(220px,1.6fr) repeat(3,1fr);border:1px solid #1d293b;border-radius:8px;background:#0a1120;margin-bottom:12px;overflow:hidden}
        .ex-week-summary>div{min-height:86px;padding:15px 18px;border-right:1px solid #1d293b;display:flex;align-items:center;gap:11px}.ex-week-summary>div:last-child{border:0}
        .ex-week-summary span{color:#94a3b8;font-size:11px}.ex-week-summary b{font-size:21px;color:#f8fafc}.ex-week-summary small{font-size:10px;color:#64748b;font-weight:500}
        .ex-week-lead{display:block!important}.ex-week-lead>span{display:block}.ex-week-lead>strong{font-size:28px;display:block;margin:4px 0 10px}.ex-week-lead strong small{font-size:12px}
        .ex-readiness{display:grid;grid-template-columns:210px minmax(0,1fr) 240px;background:#0a1120;border:1px solid #1d293b;border-radius:8px;margin-bottom:12px;overflow:hidden}.ex-readiness>div{padding:16px;border-right:1px solid #1d293b}.ex-readiness>div:last-child{border:0}.ex-readiness-score small,.ex-source-state>small{color:#64748b;font-size:9px}.ex-readiness-score strong{display:block;font-size:32px;margin:5px 0 2px}.ex-readiness-score strong span{font-size:11px;color:#64748b}.ex-readiness-score>b{font-size:10px}.ex-readiness-score>b.good{color:#34d399}.ex-readiness-score>b.moderate{color:#fbbf24}.ex-readiness-score>b.caution{color:#fb7185}.ex-score-track{height:5px;background:#172033;margin-top:10px}.ex-score-track i{display:block;height:100%;background:#38bdf8}.ex-readiness-plan header{display:flex;justify-content:space-between}.ex-readiness-plan header small{font-size:9px;color:#64748b}.ex-readiness-plan h2{font-size:14px;margin:3px 0}.ex-readiness-plan>p{font-size:10px;color:#94a3b8;line-height:1.55;margin:10px 0 0}.ex-type-tags{display:flex;gap:5px;flex-wrap:wrap;margin-top:8px}.ex-type-tags span{padding:4px 7px;border:1px solid #1e4560;background:#0d2031;color:#7dd3fc;border-radius:4px;font-size:9px}.ex-source-state>div{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:10px}.ex-source-state span{display:flex;align-items:center;gap:6px;color:#475569;font-size:9px}.ex-source-state span.on{color:#34d399}
        .ex-analysis-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}.ex-constraints>div{display:flex;gap:9px;padding:9px 0;border-top:1px solid #172033}.ex-constraints>div>span{color:#fbbf24}.ex-constraints>div.high>span{color:#fb7185}.ex-constraints b{font-size:10px}.ex-constraints small{display:block;color:#64748b;font-size:9px;line-height:1.5;margin-top:2px}.ex-analysis-ok{color:#34d399!important;font-size:10px;align-items:center}.ex-nutrition-snapshot{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:8px}.ex-nutrition-snapshot span{padding:8px;background:#0d1728;border:1px solid #1d293b;border-radius:6px}.ex-nutrition-snapshot svg{color:#38bdf8}.ex-nutrition-snapshot b{display:block;font-size:13px;margin-top:4px}.ex-nutrition-snapshot small{font-size:8px;color:#64748b}.ex-fueling>p{font-size:9px;color:#64748b;line-height:1.5;margin:7px 0}.ex-fueling>p b{color:#cbd5e1;margin-right:5px}
        .ex-performance-grid{display:grid;grid-template-columns:1.15fr 1fr;gap:12px;margin-bottom:12px}.ex-performance-grid>.ex-panel{min-width:0}.ex-performance-scores{display:grid;grid-template-columns:1fr 1fr;border:1px solid #1d293b;border-radius:7px;overflow:hidden}.ex-performance-scores>div{padding:12px}.ex-performance-scores>div+div{border-left:1px solid #1d293b}.ex-performance-scores span{display:block;color:#64748b;font-size:9px}.ex-performance-scores strong{display:block;font-size:24px;margin:4px 0}.ex-performance-scores strong small{font-size:9px;color:#64748b;margin-left:3px}.ex-performance-scores>div>b{font-size:9px}.ex-performance-scores b.good,.ex-performance-scores b.balanced{color:#34d399}.ex-performance-scores b.moderate,.ex-performance-scores b.low{color:#fbbf24}.ex-performance-scores b.caution,.ex-performance-scores b.high{color:#fb7185}.ex-score-track.recovery i{background:#34d399}.ex-load-comparison{display:flex;justify-content:space-between;margin-top:10px;color:#64748b;font-size:8px}.ex-response-facts{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;margin-top:9px}.ex-response-facts span{padding:8px;background:#0d1728;border-radius:6px;color:#64748b;font-size:8px}.ex-response-facts b{display:block;color:#cbd5e1;font-size:12px;margin-bottom:3px}.ex-zone-summary{display:flex;gap:16px;color:#64748b;font-size:9px;margin-bottom:10px}.ex-zone-summary b{color:#e5e7eb;font-size:12px;margin-left:3px}.ex-zone-list>div{display:grid;grid-template-columns:25px 95px minmax(70px,1fr) 62px;align-items:center;gap:7px;min-height:27px;font-size:9px}.ex-zone-list>div>b{color:#cbd5e1}.ex-zone-list>div>span{color:#94a3b8}.ex-zone-list>div>i{height:4px;background:#172033;overflow:hidden}.ex-zone-list>div>i em{display:block;height:100%}.ex-zone-list>div>strong{text-align:right;font-size:9px}.ex-heart-zones>p,.ex-correlation>p{color:#64748b;font-size:8px;line-height:1.5;margin:9px 0 0}.ex-correlation-chart{height:155px}.ex-correlation-chart svg{display:block;width:100%;height:135px;overflow:visible}.ex-correlation-chart .grid{stroke:#1d293b;stroke-width:1}.ex-correlation-chart polyline{fill:none;stroke-width:2;stroke-linejoin:round;stroke-linecap:round}.ex-correlation-chart .intake{stroke:#38bdf8}.ex-correlation-chart .exercise{stroke:#34d399}.ex-chart-labels{display:grid;grid-template-columns:repeat(14,1fr);font-size:7px;color:#475569;text-align:center}.ex-chart-legend{display:flex;align-items:center;gap:13px;margin-top:8px;color:#94a3b8;font-size:8px}.ex-chart-legend span{display:flex;align-items:center;gap:4px}.ex-chart-legend i{width:12px;height:2px}.ex-chart-legend i.intake{background:#38bdf8}.ex-chart-legend i.exercise{background:#34d399}.ex-chart-legend>b{margin-left:auto;color:#64748b;font-weight:500}.ex-strategy>strong{display:block;font-size:12px;line-height:1.5;margin-bottom:11px}.ex-strategy-focus{display:flex;justify-content:space-between;align-items:center;padding:8px 10px;background:#0d2031;border:1px solid #1e4560;border-radius:6px;font-size:9px;color:#7dd3fc}.ex-strategy-focus b{font-size:12px}.ex-strategy-columns{display:grid;grid-template-columns:1fr 1fr;gap:13px;margin-top:12px}.ex-strategy-columns>div+div{border-left:1px solid #1d293b;padding-left:13px}.ex-strategy-columns small{color:#64748b;font-size:8px}.ex-strategy-columns p{position:relative;padding-left:10px;color:#94a3b8;font-size:9px;line-height:1.45;margin:7px 0}.ex-strategy-columns p:before{content:'';position:absolute;left:0;top:5px;width:4px;height:4px;background:#38bdf8}
        .ex-progress{height:5px;background:#172033;overflow:hidden;border-radius:4px}.ex-progress i{display:block;height:100%;background:#38bdf8}.ex-progress.green i{background:#34d399}.ex-progress.amber i{background:#fbbf24}
        .ex-dashboard-grid{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(280px,1fr);gap:12px;margin-bottom:12px}.ex-dashboard-grid.lower{grid-template-columns:1.35fr 1fr}
        .ex-panel{background:#0a1120;border:1px solid #1d293b;border-radius:8px;padding:16px}.ex-panel>header{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:14px}
        .ex-panel header small{display:block;color:#64748b;font-size:9px;margin-bottom:3px}.ex-panel h2{font-size:14px;margin:0}.ex-panel header>span{font-size:10px;color:#64748b}.ex-panel header>button:not(.ex-primary-action){width:auto;padding:0 10px;font-size:10px}
        .ex-bars{height:150px;display:grid;grid-template-columns:repeat(7,1fr);gap:8px;align-items:end;margin-bottom:12px}.ex-bar-item{text-align:center}.ex-bar-item>span,.ex-bar-item small{font-size:9px;color:#64748b}.ex-bar-item>div{height:110px;margin:5px auto;width:min(28px,70%);background:#101a2c;display:flex;align-items:flex-end;border-radius:4px 4px 2px 2px;overflow:hidden}.ex-bar-item i{width:100%;background:#38bdf8;min-height:4px}
        .ex-goal-row{display:flex;justify-content:space-between;align-items:center;margin:10px 0 5px;font-size:10px}.ex-goal-row span{display:flex;align-items:center;gap:6px;color:#94a3b8}.ex-goal-row b{font-size:11px}
        .ex-primary-action{width:auto!important;padding:0 11px!important;display:flex!important;align-items:center;gap:5px;background:#0ea5e9!important;color:#fff!important;border:0!important;font-size:11px!important}
        .ex-recommendations{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}.ex-recommendations>div{display:flex;gap:10px;padding:12px;background:#0d1728;border:1px solid #1c2a3e;border-radius:7px}.ex-recommendations>div>span,.ex-session-icon{width:32px;height:32px;display:grid;place-items:center;background:#10253a;color:#38bdf8;border-radius:7px;flex-shrink:0}.ex-recommendations b{font-size:11px}.ex-recommendations small{display:block;color:#64748b;font-size:9px;line-height:1.45;margin-top:4px}.ex-empty-inline{grid-column:1/-1;align-items:center;color:#94a3b8;font-size:11px}
        .ex-session-row{display:flex;align-items:center;gap:10px;padding:10px 0;border-top:1px solid #172033}.ex-session-row:first-of-type{border-top:0}.ex-session-row>div{flex:1}.ex-session-row b{font-size:11px}.ex-session-row small{display:block;color:#64748b;font-size:9px;margin-top:2px}.ex-session-row>strong{font-size:14px}.ex-session-row>strong small{display:inline;margin-left:2px}
        .ex-health>div{display:flex;justify-content:space-between;align-items:center;padding:12px 0;border-top:1px solid #172033}.ex-health>div span{display:flex;align-items:center;gap:7px;color:#94a3b8;font-size:10px}.ex-health>div b{font-size:12px}.ex-health p{font-size:9px;line-height:1.6;color:#64748b;margin:10px 0 0}
        .ex-evidence{margin-bottom:12px}.ex-evidence>div{display:grid;grid-template-columns:repeat(4,1fr);gap:7px}.ex-evidence article{padding:10px;background:#0d1728;border:1px solid #1d293b;border-radius:6px;min-width:0}.ex-evidence article span{display:block;font-size:8px;color:#38bdf8}.ex-evidence article b{display:block;font-size:9px;margin:5px 0}.ex-evidence article strong{display:block;font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ex-evidence article small{font-size:8px;color:#64748b}.ex-evidence>p{font-size:8px;color:#475569;margin:12px 0 0;line-height:1.5}
        .ex-overlay{position:fixed;inset:0;z-index:1300;background:rgba(2,6,15,.86);display:flex;align-items:center;justify-content:center;padding:16px}
        .ex-dialog,.ex-history{width:min(680px,100%);max-height:90vh;overflow:auto;background:#08111f;border:1px solid #273449;border-radius:8px;padding:18px;box-sizing:border-box}.ex-dialog.compact{max-width:480px}
        .ex-dialog>header,.ex-history>header{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}.ex-dialog header small,.ex-history header small{font-size:9px;color:#38bdf8}.ex-dialog h2,.ex-history h2{font-size:16px;margin:3px 0 0}
        .ex-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:11px}.ex-form-grid label,.ex-note{display:flex;flex-direction:column;gap:5px;font-size:10px;color:#94a3b8}.ex-form-grid label>small{color:#64748b;font-size:8px;line-height:1.5}.ex-form-grid .wide{grid-column:1/-1}.ex-form-grid input,.ex-form-grid select,.ex-note textarea,.ex-set-row input{height:39px;border:1px solid #26344a;border-radius:6px;background:#0d1728;color:#e5e7eb;padding:0 10px;box-sizing:border-box;font:inherit}.ex-note{margin-top:12px}.ex-note textarea{height:auto;padding:10px;resize:vertical}
        .ex-set-editor{margin-top:14px;padding:12px;border:1px solid #223047;border-radius:7px}.ex-set-editor>div:first-child{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;font-size:11px}.ex-set-editor>div:first-child button{display:flex;align-items:center;gap:4px;border:0;background:transparent;color:#38bdf8}.ex-set-row{display:grid;grid-template-columns:24px minmax(120px,1fr) 85px 70px 28px;gap:6px;align-items:center;margin-top:6px}.ex-set-row>span{font-size:10px;color:#64748b;text-align:center}.ex-set-row>button{border:0;background:transparent;color:#64748b}.ex-error{color:#fca5a5;font-size:11px}
        .ex-dialog footer{display:flex;justify-content:flex-end;gap:8px;margin-top:16px}.ex-dialog footer button{height:38px;padding:0 18px;border:1px solid #26344a;border-radius:6px;background:#101a2c;color:#cbd5e1}.ex-dialog footer .primary{background:#0284c7;border-color:#0284c7;color:#fff}
        .ex-history-filter{display:flex;gap:6px;overflow:auto;margin-bottom:12px}.ex-history-filter button{white-space:nowrap;padding:7px 10px;border:1px solid #26344a;border-radius:6px;background:#0d1728;color:#94a3b8;font-size:10px}.ex-history-filter button.active{color:#7dd3fc;border-color:#0ea5e9}
        .ex-history-list article{position:relative;padding:13px;border:1px solid #1d293b;border-radius:7px;margin-bottom:8px;background:#0a1424}.ex-history-date{display:flex;gap:6px;align-items:center;color:#64748b;font-size:9px}.ex-history-main{display:flex;justify-content:space-between;margin:8px 32px 8px 0}.ex-history-main b{font-size:12px}.ex-history-main small{display:block;color:#64748b;font-size:9px;margin-top:2px}.ex-history-main strong{font-size:12px}.ex-history-meta{display:flex;gap:12px;color:#94a3b8;font-size:9px}.ex-history-list article p{font-size:9px;color:#64748b;margin:8px 28px 0 0}.ex-history-list article>button{position:absolute;right:10px;bottom:10px;border:0;background:transparent;color:#64748b}
        @media(max-width:680px){.ex-main{padding:12px 12px 72px}.ex-week-summary{grid-template-columns:1fr 1fr}.ex-week-summary>div{min-height:72px;padding:12px}.ex-week-lead{grid-column:1/-1;border-bottom:1px solid #1d293b}.ex-week-summary>div:nth-child(3){border-right:0}.ex-readiness{grid-template-columns:1fr 1fr}.ex-readiness-score{border-bottom:1px solid #1d293b}.ex-readiness-plan{border-right:0!important;border-bottom:1px solid #1d293b}.ex-source-state{grid-column:1/-1}.ex-source-state>div{grid-template-columns:repeat(4,1fr)}.ex-analysis-grid,.ex-performance-grid,.ex-dashboard-grid,.ex-dashboard-grid.lower{grid-template-columns:1fr}.ex-zone-list>div{grid-template-columns:24px 82px minmax(45px,1fr) 58px}.ex-strategy-columns{grid-template-columns:1fr}.ex-strategy-columns>div+div{border-left:0;border-top:1px solid #1d293b;padding:10px 0 0}.ex-evidence>div{grid-template-columns:1fr 1fr}.ex-recommendations{grid-template-columns:1fr}.ex-bars{height:130px}.ex-bar-item>div{height:90px}.ex-form-grid{grid-template-columns:1fr}.ex-form-grid .wide{grid-column:auto}.ex-set-row{grid-template-columns:22px minmax(100px,1fr) 58px 54px 24px}.ex-dialog{padding:15px}.ex-week-summary>div:last-child{border-top:1px solid #1d293b;border-right:0}}
      `}</style>
    </div>
  );
};

export default FitnessCenter;
